import { useState } from 'react'
import { useVersions } from '../../hooks/useSharing'
import { Spinner } from '../ui'
import clsx from 'clsx'

/**
 * VersionHistoryPanel
 *
 * Props:
 *   type   — 'document' | 'specification' | 'test-plan'
 *   id     — artifact id
 */
export default function VersionHistoryPanel({ type, id }) {
  const { versions, loading } = useVersions(type, id)
  const [expanded, setExpanded] = useState(null)

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <Spinner size="md" />
    </div>
  )

  if (versions.length === 0) return (
    <div className="text-center py-10 text-white/30 text-sm">
      No version history yet. Generate a document to create the first version.
    </div>
  )

  return (
    <div className="space-y-2">
      {versions.map((v, i) => {
        const isLatest   = i === 0
        const isExpanded = expanded === v.id

        return (
          <div key={v.id}
            className={clsx(
              'rounded-xl border transition-all duration-200',
              isLatest
                ? 'bg-purple-500/8 border-purple-500/25'
                : 'bg-white/[0.02] border-white/8 hover:border-white/12'
            )}
          >
            {/* Row header */}
            <button
              type="button"
              onClick={() => setExpanded(isExpanded ? null : v.id)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
            >
              {/* Version badge */}
              <div className={clsx(
                'flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold font-mono',
                isLatest
                  ? 'bg-purple-500/25 text-purple-300 border border-purple-500/30'
                  : 'bg-white/6 text-white/50 border border-white/10'
              )}>
                {v.version_label}
              </div>

              {/* Mode badge */}
              <span className={clsx(
                'flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border',
                v.is_diff
                  ? 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                  : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
              )}>
                {v.is_diff ? 'Diff' : 'Full'}
              </span>

              {/* Commit + metadata */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {v.commit_sha_short && (
                    <code className="text-[11px] text-white/40 font-mono">{v.commit_sha_short}</code>
                  )}
                  {v.changed_files_count != null && (
                    <span className="text-[11px] text-white/35">
                      {v.changed_files_count} file{v.changed_files_count !== 1 ? 's' : ''} changed
                    </span>
                  )}
                  {v.file_size && (
                    <span className="text-[11px] text-white/25">{v.file_size}</span>
                  )}
                </div>
              </div>

              {/* Right side */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Trigger pill */}
                <span className={clsx(
                  'hidden sm:block text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full',
                  v.trigger === 'webhook'   && 'text-orange-400 bg-orange-500/10',
                  v.trigger === 'schedule'  && 'text-cyan-400 bg-cyan-500/10',
                  v.trigger === 'manual'    && 'text-white/30 bg-white/5',
                  v.trigger === 'diff_auto' && 'text-blue-400 bg-blue-500/10',
                )}>
                  {v.trigger}
                </span>

                <span className="text-[11px] text-white/30 whitespace-nowrap">{v.created_at_human}</span>

                <svg className={clsx('w-4 h-4 text-white/25 transition-transform', isExpanded && 'rotate-180')}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
              </div>
            </button>

            {/* Expandable detail */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-0 space-y-3 animate-fade-in">
                <div className="h-px bg-white/6" />

                {/* Commit range */}
                {v.is_diff && v.diff_from_sha_short && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-white/30">Diff range</span>
                    <code className="text-white/50 font-mono">{v.diff_from_sha_short}</code>
                    <span className="text-white/20">→</span>
                    <code className="text-purple-400 font-mono">{v.commit_sha_short}</code>
                  </div>
                )}

                {/* Generated by */}
                {v.generated_by && (
                  <p className="text-xs text-white/30">
                    Generated by <span className="text-white/50">{v.generated_by}</span>
                  </p>
                )}

                {/* Diff summary */}
                {v.diff_summary && (
                  <div className="bg-black/20 rounded-xl p-3.5 border border-white/6">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/25 mb-2">What changed</p>
                    <pre className="text-xs text-white/55 font-mono whitespace-pre-wrap leading-relaxed">
                      {v.diff_summary}
                    </pre>
                  </div>
                )}

                {/* Download this version */}
                <div className="flex justify-end">
                  <span className="text-xs text-white/20 italic">
                    Version files stored on server — download via the main document view.
                  </span>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
